import React, { useState, useEffect } from 'react';
import {
	IonModal,
	IonContent,
	IonGrid,
	IonRow,
	IonCol,
	IonTitle,
	IonItem,
	IonLabel,
	IonInput,
	IonButton
} from '@ionic/react';

type EmailSendStatus = 'idle' | 'sending' | 'success' | 'error';

export interface EmailSendModalProps {
	isOpen: boolean;
	onDismiss: () => void;
	emailType: string; // e.g. 'licenses'
	defaultEmail?: string;
	onSend: (email: string) => Promise<void>;
	successMessageOverride?: string;
	titleOverride?: string;
}

const typeConfig: Record<
	string,
	{ title: string; prompt: string; success: (email: string) => string }
> = {
	licenses: {
		title: 'Send Licenses',
		prompt: 'Enter the email address to receive these licenses.',
		success: (e: string) => `Licenses were emailed to ${e}.`
	}
};

const EmailSendModal: React.FC<EmailSendModalProps> = ({
	isOpen,
	onDismiss,
	emailType,
	defaultEmail,
	onSend,
	successMessageOverride,
	titleOverride
}) => {
	const cfg = typeConfig[emailType] || {
		title: 'Send Email',
		prompt: 'Enter the destination email address.',
		success: (e: string) => `Email sent to ${e}.`
	};

	const [email, setEmail] = useState(defaultEmail || '');
	const [status, setStatus] = useState<EmailSendStatus>('idle');
	const [error, setError] = useState('');

	useEffect(() => {
		if (isOpen) {
			setEmail(defaultEmail || '');
			setStatus('idle');
			setError('');
		}
	}, [isOpen, defaultEmail]);

	const validate = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

	const handleSend = async () => {
		if (!validate(email)) {
			setError('Enter a valid email.');
			return;
		}
		setStatus('sending');
		setError('');
		try {
			await onSend(email.trim());
			setStatus('success');
		} catch {
			setStatus('error');
			setError('Failed to send. Try again.');
		}
	};

	const closeIfAllowed = () => {
		if (status !== 'sending') onDismiss();
	};

	return (
		<IonModal isOpen={isOpen} onDidDismiss={closeIfAllowed}>
			<IonContent>
				<IonGrid>
					<IonRow>
						<IonCol size="12">
							<IonTitle>{titleOverride || cfg.title}</IonTitle>
						</IonCol>
					</IonRow>

					{status === 'idle' && (
						<>
							<IonRow>
								<IonCol size="12">
									<p style={{ marginTop: 0 }}>{cfg.prompt}</p>
								</IonCol>
							</IonRow>
							<IonRow>
								<IonCol size="12">
									<IonItem>
										<IonLabel position="stacked">Email</IonLabel>
										<IonInput
											type="email"
											value={email}
											placeholder="name@example.com"
											onIonChange={e => setEmail(e.detail.value || '')}
										/>
									</IonItem>
									{error && (
										<p
											style={{
												color: 'var(--ion-color-danger)',
												marginLeft: 16
											}}
										>
											{error}
										</p>
									)}
								</IonCol>
							</IonRow>
							<IonRow>
								<IonCol size="6">
									<IonButton expand="block" fill="outline" onClick={onDismiss}>
										Cancel
									</IonButton>
								</IonCol>
								<IonCol size="6">
									<IonButton
										expand="block"
										onClick={handleSend}
										disabled={!email}
									>
										Send
									</IonButton>
								</IonCol>
							</IonRow>
						</>
					)}

					{status === 'sending' && (
						<IonRow>
							<IonCol class="ion-text-center">
								<p>Sending...</p>
							</IonCol>
						</IonRow>
					)}

					{status === 'success' && (
						<>
							<IonRow>
								<IonCol class="ion-text-center">
									<h2>Success</h2>
									<p>
										{successMessageOverride
											? successMessageOverride
											: cfg.success(email)}
									</p>
								</IonCol>
							</IonRow>
							<IonRow>
								<IonCol size="12">
									<IonButton expand="block" onClick={onDismiss}>
										Close
									</IonButton>
								</IonCol>
							</IonRow>
						</>
					)}

					{status === 'error' && (
						<>
							<IonRow>
								<IonCol class="ion-text-center">
									<h2 style={{ color: 'var(--ion-color-danger)' }}>Error</h2>
									<p>{error}</p>
								</IonCol>
							</IonRow>
							<IonRow>
								<IonCol size="6">
									<IonButton
										expand="block"
										fill="outline"
										onClick={() => {
											setStatus('idle');
											setError('');
										}}
									>
										Retry
									</IonButton>
								</IonCol>
								<IonCol size="6">
									<IonButton expand="block" onClick={onDismiss}>
										Close
									</IonButton>
								</IonCol>
							</IonRow>
						</>
					)}
				</IonGrid>
			</IonContent>
		</IonModal>
	);
};

export default EmailSendModal;
